import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import WaveIconForBtn from '~/assets/icons/waveIcon.svg';
import DotsIcon from '~/assets/button/dotsVertical.svg';
import SimpleAudioPlayer from '~/ui/audio/SimpleAudioPlayer';

const DotsIconForBtn = styled(DotsIcon)`
  cursor: pointer;
  margin-top: 12px;
  right: 5px;
  width: 16px;

  path {
    fill: #a9a9a9;
  }
`;

const ColorForIcon = [
  'red',
  'blue',
  'green',
  'purple',
  'orange',
  'pink',
  'brown',
  'gray',
  '#9738FF',
  '#E63A14',
  '#FF6B00',
  '#008000',
  '#0000FF',
  '#800080',
  '#FFA500',
  '#FFC0CB',
  '#A52A2A',
  '#00C9D7',
  '#D81242',
  '#134BDB',
  '#8F7000',
  '#3E8F00'
];

const getRandomColor = () => {
  return ColorForIcon[Math.floor(Math.random() * ColorForIcon.length)];
};

const WaveIcon = styled(WaveIconForBtn)<{ color?: string }>`
  rect {
    fill: ${props => props.color || '#9738ff'};
  }

  #circle {
    fill: ${props => props.color || '#9738ff'};
    opacity: 0.2;
    filter: contrast(50%);
  }
`;

const Popup = styled.div<{ top: number; left: number }>`
  position: fixed;
  top: ${props => props.top}px;
  left: ${props => props.left}px;
  /* width: 130px; */
  background-color: #fff;
  z-index: 1000;
  border-radius: 5px;
  padding: 10px 10px;
  box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.05),
    0 30px 30px 0 rgba(0, 0, 0, 0.03), 0 8px 9px 0 rgba(0, 0, 0, 0.21);

  div {
    color: #2e2e2e;
    font-family: Raleway;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
  }
`;

interface Voice {
  voice_id: string;
  voiceId?: string;
  name: string;
  gender: string;
  id?: number | string;
  previewUrl?: string;
  mediaUrl?: string;
  tags?: string[];
  customFields?: {
    color?: string;
  };
}

interface VoiceItemProps {
  voice: Voice;
  isSelected: boolean;
  onSelect: (voiceId: string) => void;
  onDotsClick?: (event: React.MouseEvent) => void;
  apiService: any;
  dontShowDots?: boolean;
  setDefaultVoice?: (voice: Voice) => void;
  stopAllAudioPlayersExcept?: (exceptVoiceId?: string) => void;
  registerAudioPlayer?: (
    voiceId: string,
    audioElement: HTMLAudioElement
  ) => void;
  unregisterAudioPlayer?: (voiceId: string) => void;
}

const VoiceItem: React.FC<VoiceItemProps> = ({
  voice,
  isSelected,
  onSelect,
  onDotsClick,
  apiService,
  dontShowDots,
  setDefaultVoice,
  stopAllAudioPlayersExcept,
  registerAudioPlayer,
  unregisterAudioPlayer
}) => {
  const voiceId = voice.voiceId || voice.voice_id;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        dotsRef.current &&
        !dotsRef.current.contains(event.target as Node)
      ) {
        setIsPopupOpen(false);
      }
    };

    if (isPopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopupOpen]);

  const handleDotsClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (dotsRef.current) {
      const rect = dotsRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom - 50,
        left: rect.left - 50 + rect.width
      });
    }

    setIsPopupOpen(!isPopupOpen);
  };

  const handlePopupMouseLeave = () => {
    setIsPopupOpen(false);
  };

  const handleMakeDefaultVoice = () => {
    setIsPopupOpen(false);
    apiService.setPreferredVoice(voice.id);

    if (setDefaultVoice) {
      setDefaultVoice(voice);
    }
  };

  return (
    <div
      className={`voice_item ${isSelected ? 'selected' : ''}`}
      key={voiceId}
      onClick={() => onSelect(voiceId)}
    >
      <div className="icon">
        <WaveIcon color={voice?.customFields?.color || getRandomColor()} />
      </div>
      <div className="name">
        <b>{voice.name}</b>
        <p>
          {voice.gender}
          {voice?.tags ? ', ' : ''}
          {voice?.tags?.join(', ')}
        </p>
      </div>
      <div className="playBtnSection">
        <SimpleAudioPlayer
          src={voice?.previewUrl || ''}
          voiceId={voiceId}
          stopAllAudioPlayersExcept={
            voiceId ? stopAllAudioPlayersExcept : undefined
          }
          registerAudioPlayer={voiceId ? registerAudioPlayer : undefined}
          unregisterAudioPlayer={voiceId ? unregisterAudioPlayer : undefined}
        />
      </div>
      <div className="dontsBtn" ref={dotsRef}>
        <DotsIconForBtn
          className="chapter_dots"
          onClick={onDotsClick || handleDotsClick}
          style={{ visibility: dontShowDots ? 'hidden' : 'visible' }}
        />
      </div>
      {isPopupOpen && (
        <Popup
          ref={popupRef}
          top={popupPosition.top}
          left={popupPosition.left}
          onMouseLeave={handlePopupMouseLeave}
        >
          <div onClick={handleMakeDefaultVoice}>Pin voice</div>
        </Popup>
      )}
    </div>
  );
};

export default VoiceItem;
